import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { applyStudentCountIfTypeMatches } from './applyStudentCount';
import { extractStudentsFromText, type FileType } from './extractStudents';
import { FILE_TYPE_OPTIONS } from './fileTypes';
import { deleteUpload, saveUpload, updateUploadKind } from '../Data/uploadsApi';
import { hydrateSisMappings, retrySisMappingSaves, sisMappingSavesSettled } from '../Data/sisMappingApi';
import { rebuildRostersFromCsvs, type UploadKind } from '../Data/uploadedFiles';
import { applyFlashlightFloor } from './flashlightFloor';
import { MatchRosterFields } from '../Data/MatchRosterFields';
import { DataHelpDrawer } from './DataHelpDrawer';
import { StackedDocsIllustration } from './StackedDocsIllustration';
import {
  applySavedSisMapping,
  proposeSisMapping,
  readSisColumns,
  saveSisMapping,
  sisColumnOverrides,
  type SisMapping,
} from './sisFieldMapping';
import { mergeWidaRosters, parseWidaHistory, scaleForUploadKind } from './widaHistory';
import { saveWidaRoster } from './studentRoster';
import {
  invalidateStagedRosterRebuilds,
  rebuildStagedRosterCaches,
  rehydrateUploadDraft,
  saveStagedBatch,
  type SelectedFile,
} from './uploadRehydrate';
import {
  addSisResolvedIds,
  clearSisResolvedIds,
  mergeSisRosters,
  parseSisRoster,
  saveSisRoster,
  saveSisRosterMapped,
  sisLoadBearingColumnsResolved,
  sisStudentCovered,
} from './sisRoster';
import {
  HELPFUL_HEADING,
  MUST_INCLUDE_HEADING,
  ROSTER_HELPFUL_KEYS,
  ROSTER_MUST_INCLUDE_KEYS,
  rosterFieldLabels,
} from './rosterFieldLabels';
import { loadReclassificationSelection } from '../../core/reclassificationSelection';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

function uploadKindFor(type: FileType | '' | undefined): UploadKind | undefined {
  if (type === 'wida_access') return 'wida_access';
  if (type === 'alternate_access_wida') return 'alternate_access_wida';
  if (type === 'sis' || type === 'student_roster') return 'student_roster';
  return undefined;
}

const INK = '#08081B';
const MUTED = '#63637D';
const ACCENT = '#2A2A8B';
const BORDER = 'rgba(8, 8, 27, 0.15)';
const PANEL_BG = '#F6F6F8';
const CHIP_BG = '#CCE8FA';
const CHIP_TEXT = '#072254';
const HELPFUL_CHIP_BG = '#E3E3E8';
const GREEN = '#52BB3A';
const ILLUS_BG = '#D4D4F2';
const SKELETON_BG = '#E5E5EA';
const ERROR_BG = '#FE8B8B';
const ERROR_TEXT = '#5B0000';

const UPLOAD_SUPPORTED_ASSESSMENTS = new Set(['WIDA ACCESS']);

const REMOVAL_ERROR = "We couldn't remove that file from your saved uploads. Please try again.";
const REBUILD_ERROR = "Couldn't refresh every roster after the delete. Please reload the page.";

type ChipGroup = {
  title: string;
  body?: string;
  chips: string[];
  variant?: 'must' | 'helpful';
};

type DataSource = {
  title: string;
  body: string;
  groups?: ChipGroup[];
};

const DATA_SOURCES: ReadonlyArray<DataSource> = [
  {
    title: 'WIDA ACCESS and Alternate ACCESS Scores',
    body: 'The original, unaltered Summative State/District Student Response file from DRC Insight (in CSV format). Upload one of each file per year — more years show more growth. Alternate ACCESS files are optional.',
  },
  {
    title: 'Student Roster (SIS Data Export)',
    body: 'One CSV file with student demographics and program data.',
    groups: [
      {
        title: MUST_INCLUDE_HEADING,
        chips: rosterFieldLabels(ROSTER_MUST_INCLUDE_KEYS),
      },
      {
        title: HELPFUL_HEADING,
        variant: 'helpful',
        chips: rosterFieldLabels(ROSTER_HELPFUL_KEYS),
      },
    ],
  },
];

function isCsv(file: File): boolean {
  return file.name.toLowerCase().endsWith('.csv');
}

function GuidancePanel({ onSeeChecklist }: { onSeeChecklist: () => void }) {
  return (
    <Stack spacing={3} sx={{ borderRadius: '8px', bgcolor: PANEL_BG, py: 2, px: 3 }}>
      {DATA_SOURCES.map((source, idx) => {
        const divider = idx < DATA_SOURCES.length - 1;
        return (
          <Box
            key={source.title}
            sx={{
              px: 1,
              pb: divider ? 3 : 0,
              borderBottom: divider ? `1px solid ${BORDER}` : 'none',
            }}
          >
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography sx={{ fontWeight: 600, fontSize: 20, lineHeight: '28px', color: INK }}>
                  {source.title}
                </Typography>
                <Typography sx={{ fontWeight: 500, fontSize: 16, lineHeight: '24px', color: MUTED }}>
                  {source.body}
                </Typography>
              </Stack>
              {source.groups?.map((group) => {
                const helpful = group.variant === 'helpful';
                return (
                  <Stack key={group.title} spacing={1}>
                    <Stack spacing={0.5}>
                      <Typography
                        sx={{ fontWeight: 700, fontSize: 16, lineHeight: '24px', color: INK }}
                      >
                        {group.title}
                      </Typography>
                      {group.body && (
                        <Typography
                          sx={{ fontWeight: 500, fontSize: 16, lineHeight: '24px', color: MUTED }}
                        >
                          {group.body}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                      {group.chips.map((chip) => (
                        <Box
                          key={chip}
                          sx={{
                            bgcolor: helpful ? HELPFUL_CHIP_BG : CHIP_BG,
                            color: helpful ? INK : CHIP_TEXT,
                            borderRadius: '999px',
                            px: helpful ? 1 : 1.5,
                            py: 0.5,
                            fontSize: 12,
                            fontWeight: 600,
                            lineHeight: '16px',
                          }}
                        >
                          {chip}
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        );
      })}
      <Box sx={{ px: 1 }}>
        <Link
          component="button"
          type="button"
          underline="hover"
          onClick={onSeeChecklist}
          sx={{ color: ACCENT, fontWeight: 700, fontSize: 15 }}
        >
          See the full checklist &amp; tips →
        </Link>
      </Box>
    </Stack>
  );
}

const WIDA_DOTS: ReadonlyArray<{ glyph: string; label: string }> = [
  { glyph: '1', label: '1st WIDA ACCESS year' },
  { glyph: '2', label: '2nd WIDA ACCESS year' },
  { glyph: '+', label: '3rd or more WIDA ACCESS years' },
];

function ProgressDot({ glyph, label, active }: { glyph: string; label: string; active: boolean }) {
  return (
    <Box
      role="img"
      aria-label={`${label} ${active ? 'uploaded' : 'not yet uploaded'}`}
      sx={{
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: '16px',
        border: active ? `2px solid ${GREEN}` : '2px dashed #9E9EB2',
        bgcolor: active ? GREEN : 'transparent',
        color: active ? '#FFFFFF' : MUTED,
      }}
    >
      <Box component="span" aria-hidden>
        {glyph}
      </Box>
    </Box>
  );
}

function ProgressTracker({ widaCount, sisDone }: { widaCount: number; sisDone: boolean }) {
  return (
    <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={4}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: '24px', color: INK }}>
          WIDA ACCESS Years
        </Typography>
        {WIDA_DOTS.map((dot, i) => (
          <ProgressDot key={dot.glyph} glyph={dot.glyph} label={dot.label} active={widaCount > i} />
        ))}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, lineHeight: '24px', color: INK }}>
          SIS Data
        </Typography>
        <ProgressDot glyph="1" label="SIS data" active={sisDone} />
      </Stack>
    </Stack>
  );
}

const SKELETON_ROWS = 6;

function FileListSkeleton() {
  return (
    <Stack spacing={1.25} role="status" aria-label="Loading your uploaded files" aria-busy="true">
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        <Box
          key={i}
          data-testid="upload-file-skeleton"
          sx={{ height: 46, borderRadius: 1.5, bgcolor: SKELETON_BG }}
        />
      ))}
    </Stack>
  );
}

function DropZone({ onPick, inputRef, onFiles }: {
  onPick: () => void;
  inputRef: RefObject<HTMLInputElement | null>;
  onFiles: (files: FileList | null) => void;
}) {
  return (
    <Box sx={{ display: 'flex', borderRadius: '8px', overflow: 'hidden' }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: 124,
          flexShrink: 0,
          bgcolor: ILLUS_BG,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <StackedDocsIllustration />
      </Box>
      <Box
        sx={{
          flex: 1,
          borderTop: `1px dashed ${BORDER}`,
          borderBottom: `1px dashed ${BORDER}`,
          borderRight: `1px dashed ${BORDER}`,
          borderLeft: { xs: `1px dashed ${BORDER}`, md: 'none' },
          borderRadius: { xs: '8px', md: '0 8px 8px 0' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 2.5,
          pt: 2,
          pb: 3,
          px: 2,
        }}
      >
        <Typography sx={{ color: MUTED, fontSize: 16, fontWeight: 500, lineHeight: '24px' }}>
          Select one or more .csv files to upload
        </Typography>
        <input ref={inputRef} type="file" multiple hidden onChange={(e) => onFiles(e.target.files)} />
        <Button
          variant="outlined"
          onClick={onPick}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 16,
            letterSpacing: '0.5px',
            borderColor: BORDER,
            color: INK,
            px: 2,
            py: 0.75,
            borderRadius: '8px',
            '&:hover': { borderColor: INK, bgcolor: 'transparent' },
          }}
        >
          Select Files
        </Button>
      </Box>
    </Box>
  );
}

function csvTextFor(type: FileType, raw: string): string {
  return type === 'flashlight_benchmark' ? applyFlashlightFloor(raw) : raw;
}

export function UploadPage() {
  const navigate = useNavigate();
  const [files, setFiles] = useState<SelectedFile[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matching, setMatching] = useState<{
    fileId: string;
    mapping: SisMapping;
  } | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(true);
  const removalErrors = useRef<Set<string>>(new Set());
  const deletedIds = useRef<Set<string>>(new Set());
  const mountedRef = useRef(true);
  const pendingDeletes = useRef(0);
  const repairNeeded = useRef(false);
  const latestRebuild = useRef<symbol | null>(null);
  const [rebuildPending, setRebuildPending] = useState(false);
  const [rebuildFailed, setRebuildFailed] = useState(false);

  const location = useLocation();
  const routeAssessment = (location.state as { assessment?: string } | null)?.assessment;
  const assessment = routeAssessment ?? loadReclassificationSelection()?.assessment ?? 'WIDA ACCESS';
  const uploadSupported = UPLOAD_SUPPORTED_ASSESSMENTS.has(assessment);

  useEffect(() => {
    void hydrateSisMappings();
  }, []);

  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;
    rehydrateUploadDraft()
      .then((rows) => {
        if (cancelled || rows.length === 0) return;
        setFiles((prev) => (prev.length === 0 ? rows : prev));
      })
      .finally(() => {
        if (!cancelled) setLoadingSaved(false);
      })
      .catch((e) => console.error('upload: rehydration settled with an unexpected rejection', e));
    return () => {
      cancelled = true;
      mountedRef.current = false;
      invalidateStagedRosterRebuilds();
      if (pendingDeletes.current > 0 || repairNeeded.current) {
        repairNeeded.current = false;
        rebuildRostersFromCsvs([]);
      }
    };
  }, []);

  const addFiles = (selected: FileList | null) => {
    if (!selected) return;
    const additions: SelectedFile[] = Array.from(selected).map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      valid: isCsv(file),
    }));
    setFiles((prev) => [...prev, ...additions]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const repairAfterUnmount = () => {
    if (pendingDeletes.current > 0 || !repairNeeded.current) return;
    repairNeeded.current = false;
    rebuildRostersFromCsvs([]);
  };

  const removeFile = (id: string) => {
    const target = files.find((f) => f.id === id);
    if (!target?.persisted) {
      setFiles((prev) => prev.filter((f) => f.id !== id));
      return;
    }
    pendingDeletes.current += 1;
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, deleting: true } : f)));
    deleteUpload(id).then(
      () => {
        pendingDeletes.current = Math.max(0, pendingDeletes.current - 1);
        deletedIds.current.add(id);
        repairNeeded.current = true;
        const hadFailed = removalErrors.current.delete(id);
        if (hadFailed && removalErrors.current.size === 0) {
          setError((prev) => (prev === REMOVAL_ERROR ? null : prev));
        }
        if (!mountedRef.current) {
          repairAfterUnmount();
          return;
        }
        setFiles((prev) => prev.filter((f) => f.id !== id));
        const token = Symbol('rebuild');
        latestRebuild.current = token;
        setRebuildPending(true);
        void rebuildStagedRosterCaches(new Set(deletedIds.current))
          .then((outcome) => {
            if (latestRebuild.current !== token) return;
            if (outcome === 'failed') {
              setRebuildFailed(true);
              setError(REBUILD_ERROR);
            }
            if (outcome === 'rebuilt') {
              setRebuildFailed(false);
              setError((prev) => (prev === REBUILD_ERROR ? null : prev));
            }
          })
          .finally(() => {
            if (latestRebuild.current === token) {
              repairNeeded.current = false;
              latestRebuild.current = null;
              setRebuildPending(false);
            }
          });
      },
      (e) => {
        console.error('upload: failed to delete persisted upload', e);
        pendingDeletes.current = Math.max(0, pendingDeletes.current - 1);
        if (!mountedRef.current) {
          repairAfterUnmount();
          return;
        }
        removalErrors.current.add(id);
        setError(REMOVAL_ERROR);
        setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, deleting: false } : f)));
      },
    );
  };

  const handleTypeChange = async (id: string, type: FileType) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === id
          ? {
              ...f,
              type,
              studentCount: undefined,
              csvText: undefined,
              sisColumns: undefined,
              sisMapping: undefined,
            }
          : f
      )
    );
    const target = files.find((f) => f.id === id);
    if (!target) return;
    try {
      const raw = await target.file.text();
      const text = csvTextFor(type, raw);
      const { rows } = extractStudentsFromText(text, type);
      setFiles((prev) => applyStudentCountIfTypeMatches(prev, id, type, rows.length));
      if (type === 'sis' || type === 'student_roster') {
        const columns = readSisColumns(raw);
        await hydrateSisMappings();
        const saved = applySavedSisMapping(columns);
        const mappedCount =
          saved.kind === 'exact'
            ? extractStudentsFromText(raw, 'sis', sisColumnOverrides(saved.mapping)).rows.length
            : undefined;
        setFiles((prev) =>
          prev.map((f) =>
            f.id === id && f.type === type
              ? {
                  ...f,
                  csvText: raw,
                  sisColumns: columns,
                  sisMapping: saved.kind === 'exact' ? saved.mapping : undefined,
                  studentCount: mappedCount ?? f.studentCount,
                }
              : f
          )
        );
        if (saved.kind !== 'exact') {
          setMatching({
            fileId: id,
            mapping: saved.kind === 'partial' ? saved.mapping : proposeSisMapping(columns),
          });
        }
      }
    } catch (e) {
      console.error('upload: failed to read file for student count', e);
    }
  };

  const openMappingDialog = (f: SelectedFile) => {
    if (!f.sisColumns) return;
    const saved = applySavedSisMapping(f.sisColumns);
    setMatching({
      fileId: f.id,
      mapping:
        f.sisMapping ??
        (saved.kind === 'none' ? proposeSisMapping(f.sisColumns) : saved.mapping),
    });
  };

  const handleMappingConfirm = () => {
    if (!matching) return;
    const { fileId, mapping } = matching;
    const target = files.find((f) => f.id === fileId);
    if (target?.sisColumns) {
      saveSisMapping(
        target.sisColumns.map((c) => c.header),
        mapping
      );
    }
    setFiles((prev) =>
      prev.map((f) => {
        if (f.id !== fileId) return f;
        const studentCount =
          f.csvText === undefined
            ? f.studentCount
            : extractStudentsFromText(f.csvText, 'sis', sisColumnOverrides(mapping)).rows.length;
        return { ...f, sisMapping: mapping, studentCount };
      })
    );
    setMatching(null);
  };

  const matchingFile = matching ? files.find((f) => f.id === matching.fileId) : undefined;

  const validFiles = files.filter((f) => f.valid && !f.deleting);
  const deletionPending = files.some((f) => f.deleting) || rebuildPending || rebuildFailed;
  const canGenerate =
    uploadSupported &&
    validFiles.length > 0 &&
    validFiles.every((f) => f.type) &&
    validFiles.every((f) => (f.type !== 'sis' && f.type !== 'student_roster') || f.sisColumns) &&
    !generating &&
    !deletionPending;

  const widaFileCount = validFiles.filter((f) => f.type === 'wida_access').length;
  const sisProvided = validFiles.some((f) => f.type === 'sis' || f.type === 'student_roster');

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setGenerating(true);
    setError(null);
    retrySisMappingSaves();
    const uploadSaves = validFiles.map((f) => {
      const kind = uploadKindFor(f.type);
      if (f.persisted) {
        const synced =
          kind && kind !== f.storedKind ? updateUploadKind(f.id, kind) : Promise.resolve();
        return synced.then(
          () => ({ id: f.id, type: f.type }),
          () => null,
        );
      }
      return saveUpload(f.file, { kind, source: f.type, page: 'matcher' }).then(
        (saved) => ({ id: saved?.id, type: f.type }),
        () => null,
      );
    });
    try {
      const inputs = await Promise.all(
        validFiles.map(async (f) => {
          const type = f.type as FileType;
          const raw = await f.file.text();
          return {
            fileId: f.id,
            name: f.file.name,
            type,
            csvText: csvTextFor(type, raw),
            sisMapping: type === 'sis' || type === 'student_roster' ? f.sisMapping : undefined,
          };
        })
      );
      const widaStudents = mergeWidaRosters(
        inputs
          .filter((f) => f.type === 'wida_access' || f.type === 'alternate_access_wida')
          .flatMap((f) => parseWidaHistory(f.csvText, scaleForUploadKind(f.type))),
      );
      saveWidaRoster(widaStudents);
      const sisInputs = inputs.filter((f) => f.type === 'sis' || f.type === 'student_roster');
      if (sisInputs.length > 0) {
        const sisParsed = sisInputs.map((f) => ({
          parsed: parseSisRoster(f.csvText, f.sisMapping),
          resolved: sisLoadBearingColumnsResolved(f.csvText, f.sisMapping),
          hasRows: f.csvText
            .trim()
            .split('\n')
            .slice(1)
            .some((line) => line.replace(/["',\s]/g, '') !== ''),
        }));
        const mergedSis = mergeSisRosters(sisParsed.flatMap((p) => p.parsed));
        saveSisRoster(mergedSis);
        const resolvedIds = new Set(
          sisParsed
            .filter((p) => p.resolved)
            .flatMap((p) => p.parsed.flatMap((s) => [s.studentId, ...s.altIds])),
        );
        clearSisResolvedIds();
        addSisResolvedIds([...resolvedIds]);
        const columnsOk =
          sisParsed.some((p) => p.resolved) &&
          sisParsed.every(
            (p) =>
              p.resolved ||
              !p.hasRows ||
              (p.parsed.length > 0 &&
                p.parsed.every((s) => [s.studentId, ...s.altIds].some((id) => resolvedIds.has(id)))),
          );
        saveSisRosterMapped(
          mergedSis.length > 0 && columnsOk && mergedSis.every(sisStudentCovered),
        );
      }
      const saveOutcomes = await Promise.all(uploadSaves);
      const persisted = saveOutcomes.map((outcome) => Boolean(outcome?.id));
      const savedCount = persisted.filter(Boolean).length;
      if (savedCount < saveOutcomes.length) {
        console.error(
          `upload: ${saveOutcomes.length - savedCount} of ${saveOutcomes.length} file save(s) failed to persist`,
        );
        setError(
          "Your data was processed, but some files couldn't be saved for later sessions. Please press Generate again.",
        );
        return;
      }
      saveStagedBatch(saveOutcomes.flatMap((o) => (o?.id ? [{ id: o.id, type: o.type }] : [])));
      const persistedIds = new Map(validFiles.map((f, i) => [f.id, saveOutcomes[i]?.id]));
      setFiles((prev) =>
        prev.map((f) => {
          const id = persistedIds.get(f.id);
          return id ? { ...f, id, persisted: true, storedKind: uploadKindFor(f.type) ?? f.storedKind } : f;
        }),
      );
      if (!(await sisMappingSavesSettled())) {
        setError('Your files were saved, but your column matching couldn’t be saved. Please try again.');
        return;
      }
      navigate('/data');
    } catch (e) {
      console.error('upload: generate failed', e);
      const outcomes = await Promise.all(uploadSaves);
      const entries = outcomes.flatMap((o) => (o?.id ? [{ id: o.id, type: o.type }] : []));
      if (entries.length === outcomes.length && entries.length > 0) saveStagedBatch(entries);
      setError(
        'Something went wrong saving your files. Please try again, or remove and re-add a file.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const hasFiles = files.length > 0;

  const dropZone = (
    <DropZone onPick={() => inputRef.current?.click()} inputRef={inputRef} onFiles={addFiles} />
  );
  const guidance = <GuidancePanel onSeeChecklist={() => setHelpOpen(true)} />;

  const fileList = (
    <Stack spacing={1.25}>
      {files.map((f) => (
        <Box
          key={f.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            rowGap: 1,
            px: 2,
            py: 1.25,
            borderRadius: 1.5,
            border: `1px solid ${BORDER}`,
            bgcolor: '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
            <DescriptionOutlinedIcon sx={{ color: MUTED, fontSize: 22, flexShrink: 0 }} />
            <Typography
              title={f.file.name}
              sx={{
                fontSize: 14,
                color: INK,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {f.file.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
            {f.valid ? (
              <>
                {(f.type === 'sis' || f.type === 'student_roster') && f.sisColumns && (
                  f.sisMapping ? (
                    <>
                      <Box
                        sx={{
                          bgcolor: CHIP_BG,
                          color: CHIP_TEXT,
                          borderRadius: 999,
                          px: 1.5,
                          py: 0.5,
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Columns matched
                      </Box>
                      <Button
                        size="small"
                        onClick={() => openMappingDialog(f)}
                        aria-label={`Edit column matching for ${f.file.name}`}
                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13, color: ACCENT }}
                      >
                        Edit
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => openMappingDialog(f)}
                      aria-label={`Match columns for ${f.file.name}`}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: 13, color: ACCENT }}
                    >
                      Match columns
                    </Button>
                  )
                )}
                <Select
                  size="small"
                  displayEmpty
                  value={f.type ?? ''}
                  onChange={(e) => handleTypeChange(f.id, e.target.value as FileType)}
                  inputProps={{ 'aria-label': `File type for ${f.file.name}` }}
                  sx={{ minWidth: 210, fontSize: 13, bgcolor: PANEL_BG }}
                >
                  <MenuItem value="" disabled>
                    Select type
                  </MenuItem>
                  {FILE_TYPE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </>
            ) : (
              <Box
                sx={{
                  bgcolor: ERROR_BG,
                  color: ERROR_TEXT,
                  borderRadius: 999,
                  px: 1.75,
                  py: 0.75,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                File not uploaded: type not recognized
              </Box>
            )}
            <IconButton
              size="small"
              onClick={() => removeFile(f.id)}
              disabled={f.deleting || generating}
              aria-label={`Remove ${f.file.name}`}
              sx={{ color: MUTED }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      ))}
    </Stack>
  );

  const emptyState = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        borderRadius: '8px',
        border: `1px dashed ${BORDER}`,
        bgcolor: '#FFFFFF',
      }}
    >
      <DescriptionOutlinedIcon sx={{ color: '#7F7F99', fontSize: 22 }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: MUTED }}>
        Your files will appear here
      </Typography>
    </Box>
  );

  const unsupportedNotice = (
    <Box
      sx={{
        px: 2.5,
        py: 2.5,
        borderRadius: 1.5,
        border: `1px solid ${BORDER}`,
        bgcolor: PANEL_BG,
      }}
    >
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: INK, mb: 0.5 }}>
        {assessment} upload support is coming soon
      </Typography>
      <Typography sx={{ fontSize: 14, color: MUTED }}>
        Data upload currently supports WIDA ACCESS districts. We&rsquo;re building support for
        your state&rsquo;s assessment and will enable it here soon.
      </Typography>
    </Box>
  );

  const sections: ReactNode = !uploadSupported ? (
    unsupportedNotice
  ) : (
    <>
      {guidance}
      {dropZone}
    </>
  );

  if (matching && matchingFile?.sisColumns && matchingFile.csvText !== undefined) {
    return (
      <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: PANEL_BG }}>
        <Sidebar sections={SIDEBAR_SECTIONS} activeId="dashboard" />
        <Box sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              maxWidth: 728,
              mx: 'auto',
              mt: { xs: 1, md: 2 },
              borderRadius: 2,
              border: `1px solid ${BORDER}`,
              boxShadow: 'none',
              bgcolor: '#FFFFFF',
            }}
          >
            <Typography sx={{ fontSize: 28, fontWeight: 600, color: INK }}>
              Match your roster columns
            </Typography>
            <Typography sx={{ mt: 0.5, fontSize: 16, color: MUTED }}>
              Tell us which of your columns hold each field we need. We&rsquo;ll remember
              these for next time you upload the same export and it will map itself.
            </Typography>
            <MatchRosterFields
              name={matchingFile.file.name}
              kind={uploadKindFor(matchingFile.type)}
              csv={matchingFile.csvText}
              mapping={matching.mapping}
              onMappingChange={(mapping) => setMatching((prev) => prev && { ...prev, mapping })}
            />
            <Box
              sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Button
                onClick={() => setMatching(null)}
                sx={{ textTransform: 'none', fontWeight: 600, color: ACCENT }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={matching.mapping.student_id === undefined}
                onClick={handleMappingConfirm}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  borderRadius: '8px',
                  bgcolor: ACCENT,
                  '&:hover': { bgcolor: '#222270' },
                  '&.Mui-disabled': { bgcolor: '#C1C1CD', color: '#7F7F99' },
                }}
              >
                Submit
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: PANEL_BG }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="dashboard" />
      <Box sx={{ flex: 1, p: { xs: 3, md: 5 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            maxWidth: 728,
            mx: 'auto',
            mt: { xs: 1, md: 2 },
            borderRadius: 2,
            border: `1px solid ${BORDER}`,
            boxShadow: 'none',
            bgcolor: '#FFFFFF',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: INK, mb: 3 }}>
            Upload Your Data
          </Typography>

          <Stack spacing={3}>{sections}</Stack>

          {error && (
            <Box
              role="alert"
              sx={{
                mt: 3,
                px: 2,
                py: 1.25,
                borderRadius: 1.5,
                border: '1px solid #F90101',
                bgcolor: '#FDE3E3',
                color: ERROR_TEXT,
                fontSize: 14,
              }}
            >
              {error}
            </Box>
          )}

          <DataHelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />

          {uploadSupported && (
            <Box sx={{ pt: 3 }}>
              <Stack spacing={1.5}>
                <ProgressTracker widaCount={widaFileCount} sisDone={sisProvided} />
                {hasFiles ? fileList : loadingSaved ? <FileListSkeleton /> : emptyState}
              </Stack>
            </Box>
          )}

          <Stack direction="row" alignItems="center" sx={{ pt: 4 }}>
            <Link
              component="button"
              underline="hover"
              onClick={() => navigate(-1)}
              sx={{ color: ACCENT, fontWeight: 600, fontSize: 14 }}
            >
              Back
            </Link>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              disabled={!canGenerate}
              onClick={handleGenerate}
              sx={{
                bgcolor: ACCENT,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 1.5,
                '&:hover': { bgcolor: '#222270' },
                '&.Mui-disabled': { bgcolor: '#C1C1CD', color: '#7F7F99' },
              }}
            >
              Next
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
}
